import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Modal, Box, SelectChangeEvent, Stack } from '@mui/material'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  addIncidentsToAction,
  getIncidentByActionId,
  removeIncidentsFromAction,
} from '../../redux/incidents/incidentActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import SelectIncident from './SelectIncident'
import UseAlert from '../../components/common/alert'
import {
  DetailButton,
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPagePill,
  PRIORITY_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { formatTitle } from '../../utils/formatTitle'
import '../../components/common/ListPageTable.css'

interface IncidentListProps {
  ActionId: string
  MissionId: string
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  borderRadius: '12px',
}

const IncidentListByAction: React.FC<IncidentListProps> = ({
  ActionId,
  MissionId,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const incidentsList = useSelector(
    (state: RootState) => state.incident.sourceIncidents
  )
  const loading = useSelector((state: RootState) => state.incident.loading)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [SourceIncident, setSourceIncident] = useState<string[]>([])
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  useEffect(() => {
    if (ActionId) dispatch(getIncidentByActionId(ActionId))
  }, [dispatch, ActionId])

  const rows = useMemo(() => {
    const list = Array.isArray(incidentsList) ? incidentsList : []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((i: any) =>
      (i.IncidentName || '').toLowerCase().includes(q)
    )
  }, [incidentsList, search])

  const handleDeleteClick = async (_id: string) => {
    try {
      await dispatch(
        removeIncidentsFromAction({
          SourceIncident: { incidentId: _id },
          _id: ActionId,
        })
      )
      dispatch(getIncidentByActionId(ActionId))
      setAlert({
        open: true,
        message: 'delete successfully!',
        severity: 'success',
      })
    } catch {
      setAlert({
        open: true,
        message: 'Error deleting action. Please try again later.',
        severity: 'error',
      })
    }
  }

  const handleAddIncidentsToAction = async () => {
    try {
      if (SourceIncident.length && ActionId) {
        await dispatch(
          addIncidentsToAction({
            SourceIncident,
            _id: ActionId,
          })
        )
        await dispatch(getIncidentByActionId(ActionId))
        setAlert({
          open: true,
          message: 'Incident mise à jour avec succès!',
          severity: 'success',
        })
        setOpen(false)
        setSourceIncident([])
      } else {
        setAlert({
          open: true,
          message: 'Veuillez remplir tous les champs.',
          severity: 'warning',
        })
      }
    } catch {
      setAlert({
        open: true,
        message:
          'Erreur lors de la mise à jour de l incident. Veuillez réessayer plus tard.',
        severity: 'error',
      })
    }
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />
      <DetailEntityPanel
        listTitle={t('Incidents List', { defaultValue: 'Incidents list' })}
        searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
        searchValue={search}
        onSearchChange={setSearch}
        addLabel={t('buttons.addIncident', { defaultValue: 'Add New Incident' })}
        onAdd={() => setOpen(true)}
        canAdd={canManageMission(roles)}
      >
        <DetailTable<any>
          loading={loading && !rows.length}
          loadingLabel={t('loading')}
          emptyTitle={t('missionDetailsPage.empty.title', {
            defaultValue: 'Nothing here yet',
          })}
          emptyMessage={t('taskDetailsPage.empty.incidents', {
            defaultValue: 'No incident is linked to this task yet.',
          })}
          rows={rows}
          rowKey={(r) => (r._id || r.id) as string}
          columns={[
            {
              key: 'name',
              header: t('tables.incidentTable.name', {
                defaultValue: 'Incident name',
              }),
              render: (r) => (
                <NavLink
                  to={`/incident-detail/${r._id}`}
                  className="incyia-name-link"
                >
                  <ListPageDescription
                    value={
                      r.IncidentName
                        ? formatTitle(r.IncidentName)
                        : undefined
                    }
                    tone="primary"
                  />
                </NavLink>
              ),
            },
            {
              key: 'desc',
              header: t('Description', { defaultValue: 'Description' }),
              render: (r) => <ListPageDescription value={r.Description} />,
            },
            {
              key: 'mission',
              header: t('Mission Name', { defaultValue: 'Mission name' }),
              render: (r) => (
                <ListPageDescription
                  value={
                    r.MissionName ? formatTitle(r.MissionName) : undefined
                  }
                />
              ),
            },
            {
              key: 'crit',
              header: t('Criticality', { defaultValue: 'Criticality' }),
              render: (r) => {
                const c = Number(r.Criticality)
                const style = PRIORITY_PILL[c] ?? PRIORITY_PILL[4]
                return (
                  <ListPagePill
                    label={
                      t(`priority.${c}`, {
                        defaultValue: style.label,
                      }) || style.label
                    }
                    bg={style.bg}
                    color={style.color}
                  />
                )
              },
            },
          ]}
          actionsHeader={t('Actions', { defaultValue: 'Action' })}
          renderActions={(r) => (
            <DetailRowActions
              roles={roles}
              onEdit={() => navigate(`/incident/${r._id}`)}
              onDelete={() => handleDeleteClick(r._id as string)}
              deleteItem={{
                entityType: t('incidents', { defaultValue: 'Incident' }),
                name: r.IncidentName
                  ? formatTitle(r.IncidentName)
                  : undefined,
                details: [
                  {
                    label: t('deleteConfirm.fields.mission'),
                    value: r.MissionName
                      ? formatTitle(r.MissionName)
                      : '—',
                  },
                  {
                    label: t('deleteConfirm.fields.status'),
                    value: r.Status || '—',
                  },
                ],
              }}
            />
          )}
        />
      </DetailEntityPanel>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle}>
          <h4 style={{ margin: '0 0 16px' }}>{t('buttons.addIncident')}</h4>
          <Stack spacing={2}>
            <SelectIncident
              value={SourceIncident}
              onChange={(e: SelectChangeEvent<string[]>) =>
                setSourceIncident(e.target.value as string[])
              }
              MissionId={MissionId}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <DetailButton onClick={handleAddIncidentsToAction}>
                {t('buttons.add')}
              </DetailButton>
              <DetailButton variant="ghost" onClick={() => setOpen(false)}>
                {t('buttons.cancel')}
              </DetailButton>
            </div>
          </Stack>
        </Box>
      </Modal>
    </>
  )
}

export default IncidentListByAction
